import { format } from 'date-fns-tz';
import { transformForSubmit } from 'platform/forms-system/src/js/helpers';
import { updateName } from '../utils/transformHelpers';
import { medicalExpenseTypeLabels } from '../utils/labels';

const renameKeyInExpenses = (expenses, fromKey, toKey) => {
  if (!Array.isArray(expenses)) return expenses;
  return expenses.map(expense => {
    if (expense?.[fromKey] === undefined) return expense;
    const { [fromKey]: value, ...rest } = expense;
    return { ...rest, [toKey]: value };
  });
};

function swapNames(formData) {
  const parsedFormData = JSON.parse(formData);
  const transformedValue = { ...parsedFormData };

  // Swap claimant and veteran names if claimant is veteran
  if (
    parsedFormData?.claimantNotVeteran === false &&
    parsedFormData.claimantFullName?.first &&
    parsedFormData.claimantFullName?.last &&
    !parsedFormData.veteranFullName?.first &&
    !parsedFormData.veteranFullName?.last
  ) {
    transformedValue.veteranFullName = updateName(
      parsedFormData.claimantFullName,
    );
    delete transformedValue.claimantFullName;
  } else {
    if (parsedFormData?.veteranFullName) {
      transformedValue.veteranFullName = updateName(
        parsedFormData.veteranFullName,
      );
    }
    if (parsedFormData?.claimantFullName) {
      transformedValue.claimantFullName = updateName(
        parsedFormData.claimantFullName,
      );
    }
  }

  return JSON.stringify(transformedValue);
}

function splitVaSsnField(formData) {
  const parsedFormData = JSON.parse(formData);
  const transformedValue = { ...parsedFormData };
  if (parsedFormData?.veteranSocialSecurityNumber?.ssn) {
    transformedValue.veteranSocialSecurityNumber =
      parsedFormData?.veteranSocialSecurityNumber?.ssn;
  } else {
    delete transformedValue.veteranSocialSecurityNumber;
  }
  if (parsedFormData?.veteranSocialSecurityNumber?.vaFileNumber) {
    transformedValue.vaFileNumber =
      parsedFormData?.veteranSocialSecurityNumber?.vaFileNumber;
  }
  return JSON.stringify(transformedValue);
}

function switchToInternationalPhone(formData) {
  const parsedFormData = JSON.parse(formData);
  const transformedValue = parsedFormData;
  if (!parsedFormData?.primaryPhone) {
    return JSON.stringify(transformedValue);
  }
  if (parsedFormData?.primaryPhone?.countryCode !== 'US') {
    const callingCode = parsedFormData.primaryPhone?.callingCode || '';
    const contact = parsedFormData.primaryPhone?.contact || '';
    transformedValue.primaryPhone.contact = `+${callingCode}-${contact}`;
  }
  return JSON.stringify(transformedValue);
}

function renameExpenseConditionalFields(formData) {
  const transformedValue = JSON.parse(formData);

  transformedValue.careExpenses = renameKeyInExpenses(
    transformedValue.careExpenses,
    'fullNameRecipient',
    'recipientName',
  );
  transformedValue.medicalExpenses = renameKeyInExpenses(
    transformedValue.medicalExpenses,
    'fullNameRecipient',
    'recipientName',
  );
  transformedValue.mileageExpenses = renameKeyInExpenses(
    transformedValue.mileageExpenses,
    'fullNameTraveler',
    'travelerName',
  );
  transformedValue.mileageExpenses = renameKeyInExpenses(
    transformedValue.mileageExpenses,
    'otherTravelLocation',
    'travelLocationOther',
  );

  return JSON.stringify(transformedValue);
}

function transformWeeklyHoursToNumber(formData) {
  const parsedFormData = JSON.parse(formData);
  const transformedValue = parsedFormData;

  // Transform weeklyHours from string to number in careExpenses
  if (Array.isArray(transformedValue.careExpenses)) {
    transformedValue.careExpenses = transformedValue.careExpenses.map(
      expense => {
        if (expense?.weeklyHours === undefined) return expense;
        const weeklyHoursNumber = Number(expense.weeklyHours);
        return { ...expense, weeklyHours: weeklyHoursNumber };
      },
    );
  }

  return JSON.stringify(transformedValue);
}

function transformMedicalExpenses(formData) {
  const parsedFormData = JSON.parse(formData);
  const transformedValue = { ...parsedFormData };
  if (parsedFormData?.medicalExpenses?.length) {
    transformedValue.medicalExpenses = parsedFormData.medicalExpenses.map(
      expense => {
        const transformedExpense = { ...expense };
        if (medicalExpenseTypeLabels[expense.purpose]) {
          transformedExpense.purpose =
            medicalExpenseTypeLabels[expense.purpose]?.label;
        }
        return transformedExpense;
      },
    );
  }
  return JSON.stringify(transformedValue);
}

export const transform = (formConfig, form) => {
  let transformedData = transformForSubmit(formConfig, form);
  transformedData = swapNames(transformedData);
  transformedData = splitVaSsnField(transformedData);
  transformedData = switchToInternationalPhone(transformedData);
  transformedData = renameExpenseConditionalFields(transformedData);
  transformedData = transformWeeklyHoursToNumber(transformedData);
  transformedData = transformMedicalExpenses(transformedData);
  return JSON.stringify({
    medicalExpenseReportsClaim: {
      form: transformedData,
    },
    localTime: format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX"),
  });
};
